import { type List } from 'immutable';

import type { DsTableRow } from 'src/component/ds/dataTable/types';
import { deviceListItemKey, type DeviceListCounts, type DeviceListItem } from 'src/service/api/deviceList';
import {
  toSeverityCounts,
  type AlarmsToTriageSegment
} from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';

interface RowCounts {
  severities: AlarmsToTriageSegment[];
  workOrders: number;
}

export type DeviceListLevels = {
  controller: RowCounts & {
    group: string;
    connection: string;
    area: string;
    panel: string;
    tag: string;
    description: string | null;
    model: string | null;
    offline: boolean;
    /** Only a standalone circuit; a controller can't be forced on. */
    forcedOn: boolean;
    loto: boolean;
    /** Null for a standalone circuit (no controller), which shows in the Circuit column. */
    circuits: { shown: number; total: number } | null;
    href: string;
  };
  circuit: RowCounts & {
    tag: string;
    description: string | null;
    offline: boolean;
    forcedOn: boolean;
    loto: boolean;
    href: string;
  };
};

export type DeviceListRow = DsTableRow<DeviceListLevels>;

const toRowCounts = (counts: DeviceListCounts, labels: List<string>): RowCounts => ({
  severities: toSeverityCounts(counts.alarms.byCriticality, labels),
  workOrders: counts.workOrders,
});

export function toDeviceListRows (items: DeviceListItem[], labels: List<string>): DeviceListRow[] {
  return items.map((item) => {
    const standalone = item.kind === 'standaloneCircuit';
    // A grouped list can hold the same device once per group.
    const key = deviceListItemKey(item);

    return {
      id: `controller-${key}`,
      level: 'controller',
      data: {
        ...toRowCounts(item.rollup, labels),
        // '' rather than null, so hideRepeats compares missing values as equal.
        group: item.group?.name ?? '',
        connection: item.commLoop?.tag ?? '',
        area: item.unit ?? '',
        panel: item.panel?.tag ?? '',
        tag: item.tag,
        description: item.description,
        model: item.model,
        offline: item.offline,
        // A controller's forcedOn is its circuits' rollup, not its own state.
        forcedOn: standalone && item.forcedOn,
        loto: item.loto,
        circuits: standalone ? null : { shown: item.circuits.length, total: item.circuitCount },
        href: standalone ? `/devices/_/${encodeURIComponent(item.tag)}` : `/devices/${encodeURIComponent(item.tag)}`,
      },
      children: item.circuits.map((circuit) => ({
        id: item.group ? `circuit-${item.group.id}:${circuit.id}` : `circuit-${circuit.id}`,
        level: 'circuit',
        data: {
          ...toRowCounts(circuit, labels),
          tag: circuit.tag,
          description: circuit.description,
          offline: circuit.offline,
          forcedOn: circuit.forcedOn,
          loto: circuit.loto,
          href: `/devices/${encodeURIComponent(item.tag)}/${encodeURIComponent(circuit.tag)}`,
        },
      })),
    };
  });
}
